import { clientFetch } from "./clientFetch";

const excludedUnits = ["fl oz", "tbsp", "tsp", "ml", "oz", "g", "lb"];

/**
 * Food types whose macros are per serving (or per batch) of a recipe, scaled by servings
 * instead of by weight/volume. external_recipe = Spoonacular recipe copied into a plan or meal.
 * @param {string} type
 * @returns {boolean}
 */
export const isRecipeType = (type) =>
  type === "recipe" || type === "external_recipe";

/** Page size for generic search. */
export const SEARCH_RESULTS_LIMIT = 100;

/**
 * Function to round only values > 0
 * @param {*} value
 * @returns
 */
export const customRound = (value) => {
  const num = Number(value);
  if (isNaN(num) || num < 0) {
    return 0;
  }
  // Always round to 0 decimal places (integers)
  return Math.round(num);
};

/**
 * Request the custom and generic foods for the search results
 * @param {string} searchValue
 * @param {string[]} sourceTags
 * @param {Function} api
 * @param {Object} pagination - Optional pagination for "load more"
 * @param {number} [pagination.verifiedOffset]
 * @param {number} [pagination.generalOffset]
 * @param {number} [pagination.limit]
 * @returns {Promise<{ data: Array, hasMore: boolean, nextVerifiedOffset: number, nextGeneralOffset: number }>}
 */
export const getFilteredFoods = async (
  searchValue,
  sourceTags,
  api,
  pagination = {}
) => {
  const {
    verifiedOffset = 0,
    generalOffset = 0,
    limit = SEARCH_RESULTS_LIMIT,
  } = pagination;
  const query = { searchValue, sourceTags, limit };
  if (verifiedOffset > 0) query.verifiedOffset = verifiedOffset;
  if (generalOffset > 0) query.generalOffset = generalOffset;

  const res = await api("search-generic-foods", { query });
  const resJSON = await res.json();
  const data = resJSON?.data || [];
  const hasMore = resJSON?.hasMore ?? false;
  const nextVerifiedOffset = resJSON?.nextVerifiedOffset ?? 0;
  const nextGeneralOffset = resJSON?.nextGeneralOffset ?? 0;

  return {
    data,
    hasMore,
    nextVerifiedOffset,
    nextGeneralOffset,
  };
};

/**
 * Calculate the nutrients values of foods, meals and meal planners
 * @param {*} foods
 * @param {*} foodKey
 * @returns
 */
export const calculateMacros = ({ isMeal, isRecipe, foods, itemDetails }) => {
  const {
    itemProteins = 0,
    itemCarbs = 0,
    itemFats = 0,
    itemCalories = 0,
    itemFibers = 0,
    foodKey = "foodDetails",
  } = itemDetails;
  if (isMeal) {
    const totals = foods.reduce(
      (totals, food) => {
        const {
          quantityNumber,
          unitOfMeasure,
          type,
          [foodKey]: foodItem,
        } = food;
        if (!foodItem) return totals;
        if (isRecipeType(type)) {
          const recipeMacrosBase = foodItem.quantityDescription || "serving";
          const recipeFactor =
            recipeMacrosBase === "serving"
              ? quantityNumber || 1
              : quantityNumber / (foodItem.quantityNumber || 1);
          return {
            proteins: totals.proteins + foodItem.proteins * recipeFactor,
            carbs: totals.carbs + foodItem.carbs * recipeFactor,
            fats: totals.fats + foodItem.fats * recipeFactor,
            fibers: totals.fibers + foodItem.fibers * recipeFactor,
            calories: totals.calories + foodItem.calories * recipeFactor,
          };
        }

        const {
          proteins = 0,
          carbs = 0,
          fats = 0,
          calories = 0,
          fibers = 0,
          measurementsValue = 1,
        } = foodItem;

        let factor = 1;

        if (unitOfMeasure === "g" || unitOfMeasure === "ml") {
          factor = quantityNumber / measurementsValue;
        } else if (unitOfMeasure in conversionRates) {
          const { convertedValue } = convertMeasurement(
            quantityNumber,
            unitOfMeasure
          );
          factor = convertedValue / measurementsValue;
        } else {
          const unitValue = calculateMeasurementPerUnit(
            foodItem.quantityNumber,
            measurementsValue
          );
          factor = (quantityNumber * unitValue) / measurementsValue;
        }
        return {
          proteins: totals.proteins + proteins * factor,
          carbs: totals.carbs + carbs * factor,
          fats: totals.fats + fats * factor,
          fibers: totals.fibers + fibers * factor,
          calories: totals.calories + calories * factor,
        };
      },
      { proteins: 0, carbs: 0, fats: 0, fibers: 0, calories: 0 }
    );
    return {
      proteins: customRound(totals.proteins),
      carbs: customRound(totals.carbs),
      fats: customRound(totals.fats),
      fibers: customRound(totals.fibers),
      calories: customRound(totals.calories),
    };
  }

  if (isRecipe) {
    return {
      proteins: itemProteins,
      carbs: itemCarbs,
      fats: itemFats,
      calories: customRound(itemProteins * 4 + itemCarbs * 4 + itemFats * 9),
    };
  }

  return {
    proteins: itemProteins,
    carbs: itemCarbs,
    fats: itemFats,
    fibers: itemFibers,
    calories: itemCalories,
  };
};

/**
 * Function to recalculate the nutrients values with a new serving size
 * @param {*} param0
 * @returns
 */
export const reCalculateMacros = ({ itemDetails }) => {
  const {
    itemProteins = 0,
    itemCarbs = 0,
    itemFats = 0,
    itemFibers = 0,
    itemCalories = 0,
    quantityNumber = 1,
    customQuantityNumber = 1,
    measurementsValue = 1,
    customUnitOfMeasure = "custom",
    type,
    skipRecalculation = false,
    recipeMacrosBase,
    macrosScaledToQuantity,
  } = itemDetails;

  if (skipRecalculation) {
    return {
      proteins: itemProteins,
      carbs: itemCarbs,
      fats: itemFats,
      fibers: itemFibers,
      calories: itemCalories,
    };
  }

  // Items loaded from a saved meal plan arrive with macros already scaled to
  // `macrosScaledToQuantity` (the saved quantity), not per base unit. Since the
  // factors below are all linear in customQuantityNumber, re-portioning them is
  // just a linear ratio against that saved quantity. Using the regular factor
  // logic here would scale the already-scaled macros a second time.
  if (macrosScaledToQuantity && Number(macrosScaledToQuantity) > 0) {
    const ratio = customQuantityNumber / macrosScaledToQuantity;
    return {
      proteins: customRound(itemProteins * ratio),
      carbs: customRound(itemCarbs * ratio),
      fats: customRound(itemFats * ratio),
      fibers: customRound(itemFibers * ratio),
      calories: customRound(itemCalories * ratio),
    };
  }

  let factor;

  if (isRecipeType(type)) {
    if (recipeMacrosBase === "serving") {
      factor = customQuantityNumber;
    } else {
      factor = customQuantityNumber / quantityNumber;
    }
  } else if (customUnitOfMeasure === "g" || customUnitOfMeasure === "ml") {
    //if the user selects g or ml we are using measurementsValue
    factor = customQuantityNumber / measurementsValue;
  } else if (customUnitOfMeasure in conversionRates) {
    //of the user selects another unit, we are converting first to g or ml
    const { convertedValue } = convertMeasurement(
      customQuantityNumber,
      customUnitOfMeasure
    );
    factor = convertedValue / measurementsValue;
  } else {
    //custom case
    const unitValue = calculateMeasurementPerUnit(
      quantityNumber,
      measurementsValue
    );
    factor = (customQuantityNumber * unitValue) / measurementsValue;
  }

  const updatedValues = {
    proteins: customRound(itemProteins * factor),
    carbs: customRound(itemCarbs * factor),
    fats: customRound(itemFats * factor),
    fibers: customRound(itemFibers * factor),
    calories: customRound(itemCalories * factor),
  };
  return updatedValues;
};

/**
 * Parse the foods that are inside of a meal (get)
 * @param {*} foods
 * @returns
 */
export const transformFoodsToGetTheMeal = (foods = []) => {
  return foods.map(({ quantityNumber, type, foodDetails, unitOfMeasure }) => ({
    customQuantityNumber: quantityNumber,
    customUnitOfMeasure: unitOfMeasure,
    type,
    ...foodDetails,
  }));
};

/**
 * Combines the transformation of foods and recalculates their macros (for the meals in the food library)
 * @param {Array} foods - Array of food items in a meal
 * @returns {Array} - Transformed food items with recalculated macros
 */
export const transformMealFoods = (foods = []) => {
  return foods.map(({ quantityNumber, type, foodDetails, unitOfMeasure }) => {
    const transformed = {
      customQuantityNumber: quantityNumber,
      customUnitOfMeasure: unitOfMeasure,
      type,
      ...foodDetails,
    };

    const {
      proteins,
      carbs,
      fats,
      calories,
      fibers,
      quantityDescription,
      ...rest
    } = transformed;
    const itemDetails = {
      itemProteins: proteins,
      itemCarbs: carbs,
      itemFats: fats,
      itemCalories: calories,
      itemFibers: fibers,
      ...rest,
      foodKey: "foodId",
      skipRecalculation: false,
      ...(isRecipeType(type) && { recipeMacrosBase: quantityDescription }),
    };

    const recalculatedMacros = reCalculateMacros({ itemDetails });

    return {
      ...transformed,
      ...recalculatedMacros,
    };
  });
};

/**
 * Function to round the values
 * @param {*} value
 * @returns
 */
export const formatNutritionValue = (value) =>
  value % 1 === 0 ? value : value.toFixed(1);

export const weightUnits = ["g", "oz", "lb"];
export const volumeUnits = ["ml", "tsp", "tbsp", "fl oz", "cup"];

export const completeWeightUnits = [
  { value: "g", text: "g" },
  { value: "oz", text: "oz - 28.35g" },
  { value: "lb", text: "lb - 453.59g" },
];

export const completeVolumeUnits = [
  { value: "ml", text: "ml" },
  { value: "tsp", text: "tsp - 4.93ml" },
  { value: "tbsp", text: "tbsp - 14.79ml" },
  { value: "fl oz", text: "fl oz - 29.57ml" },
  { value: "cup", text: "cup - 236.59ml" },
];

/**
 * Get the measurement for custom cases
 * @param {*} quantityNumber
 * @param {*} measurementsValue
 * @returns
 */
export const calculateMeasurementPerUnit = (
  quantityNumber,
  measurementsValue
) => {
  if (quantityNumber === 0) return null;
  const result = measurementsValue / quantityNumber;
  return Number.isInteger(result) ? result : parseFloat(result.toFixed(2));
};

/**
 * Suffix for meal-plan UI when row unit is "custom": scaled catalog measurement (same scale as
 * reCalculateMacros custom branch: (mealQty / foodId.quantityNumber) * measurementsValue).
 * Uses foodId.unitOfMeasure as displayed (no translation).
 * @param {number|string} mealQuantityNumber
 * @param {string} [mealUnitOfMeasure]
 * @param {{ quantityNumber?: number|string, measurementsValue?: number|string, unitOfMeasure?: string }|null|undefined} foodId
 * @returns {string} e.g. " (100 g)" or ""
 */
export const formatCustomUnitMeasurementEquivalenceSuffix = (
  mealQuantityNumber,
  mealUnitOfMeasure,
  foodId
) => {
  if (mealUnitOfMeasure !== "custom" || !foodId) return "";

  const mealQty = Number(mealQuantityNumber);
  if (!Number.isFinite(mealQty) || mealQty <= 0) return "";

  const catalogQty = Number(foodId.quantityNumber);
  const safeCatalogQty =
    Number.isFinite(catalogQty) && catalogQty > 0 ? catalogQty : 1;

  const mv = Number(foodId.measurementsValue);
  if (!Number.isFinite(mv) || mv <= 0) return "";

  const unit = foodId.unitOfMeasure;
  if (typeof unit !== "string" || !unit.trim()) return "";

  const scaled = (mealQty / safeCatalogQty) * mv;
  if (!Number.isFinite(scaled) || scaled <= 0) return "";

  const rounded = Number.isInteger(scaled)
    ? scaled
    : Number.parseFloat(scaled.toFixed(2));
  return ` (${rounded}${unit})`;
};

export const conversionRates = {
  oz: 28.35, // 1 oz = 28.35 g
  lb: 453.59, // 1 lb = 453.59 g
  tsp: 4.93, // 1 tsp = 4.93 ml
  tbsp: 14.79, // 1 tbsp = 14.79 ml
  "fl oz": 29.57, // 1 fl oz = 29.57 ml
  cup: 236.59, // 1 cup = 236.59 ml
};

/**
 * Function to handle the unit conversions
 * @param {*} value
 * @param {*} unit
 * @returns
 */
export const convertMeasurement = (value, unit) => {
  if (weightUnits.includes(unit)) {
    return {
      convertedValue: value * (conversionRates[unit] || 1),
      convertedUnit: "g",
    };
  } else if (volumeUnits.includes(unit)) {
    return {
      convertedValue: value * (conversionRates[unit] || 1),
      convertedUnit: "ml",
    };
  }
  return { convertedValue: value, convertedUnit: unit };
};

/**
 * Parse the food info to edit a food day
 * @param {*} foods
 * @returns
 */
export const parseFoodData = (foods) => {
  return foods
    .map(({ quantityNumber, unitOfMeasure, type, foodId, mealId }, index) => ({
      foodId,
      item: {
        ...foodId,
        ...(mealId ? { mealId } : {}),
        customQuantityNumber: quantityNumber,
        customUnitOfMeasure: unitOfMeasure,
        type: type,
        skipRecalculation: true,
        // The API (processMealPlan) already scaled foodId macros to this saved
        // quantity, so re-portioning must use it as the base instead of the
        // catalog per-unit values.
        macrosScaledToQuantity: quantityNumber,
        // Index in the stored meal.foods (before filtering), used by the API to find this food
        foodIndex: index,
      },
    }))
    .filter(({ foodId }) => foodId && Object.keys(foodId).length > 0)
    .map(({ item }) => item);
};
/**
 * Function to pluralize the unit of measure of the food
 * @param {*} desc
 * @returns
 */

export const pluralizeIfNeeded = (desc, quantityNumber) =>
  quantityNumber > 1 && !desc.endsWith("s") && !excludedUnits.includes(desc) // evita pluralizar si está en la lista
    ? `${desc}s`
    : desc;

/**
 * Function to show the unit of the food
 * @param {*} quantityNumber
 * @param {*} type
 * @param {*} customUnitOfMeasure
 * @param {*} quantityDescription
 * @returns
 */

export const parseUnitToShow = (
  quantityNumber,
  type,
  customUnitOfMeasure,
  quantityDescription
) => {
  if (customUnitOfMeasure !== "custom") {
    return customUnitOfMeasure;
  }

  if (!quantityDescription) {
    return quantityNumber > 1 ? "servings" : "serving";
  }

  const lowerDesc = quantityDescription.toLowerCase();

  if (lowerDesc === "batch" && isRecipeType(type)) {
    return quantityNumber > 1 ? "batches" : "batch";
  }

  if (lowerDesc === "serving" || lowerDesc === "servings") {
    return quantityNumber > 1 ? "servings" : "serving";
  }

  return pluralizeIfNeeded(lowerDesc, quantityNumber);
};

/**
 * Parse the food information inside the meals
 * @param {*} foods
 * @returns
 */
export const transformFoodsForMeals = (foods) =>
  foods.map((food) => ({
    type: food.type,
    foodId: food._id,
    quantityNumber: Number(food.customQuantityNumber),
    unitOfMeasure: food.customUnitOfMeasure,
  }));

/**
 * Function to know if a date is valid
 * @param {*} date
 * @returns
 */
export const isValidDate = (date) =>
  /^\d{8}$/.test(date) ||
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(date);

/**
 * Function to get the position of a meal inside a week (indexes of the stored arrays)
 * @param {*} selectedWeek
 * @param {*} mealPlannerDate
 * @param {*} mealPlannerMeal
 * @returns {{ dayIndex: number, mealIndex: number }} -1 when not found
 */
export const getMealLocationForAddModal = (
  selectedWeek,
  mealPlannerDate,
  mealPlannerMeal
) => {
  const isDateValid = isValidDate(mealPlannerDate);

  const dayIndex = isDateValid
    ? selectedWeek.findIndex(({ date }) => date === mealPlannerDate)
    : (() => {
        const match = mealPlannerDate?.match(/Day (\d+)/);
        if (!match) return -1;
        const index = Number(match[1]) - 1;
        return selectedWeek[index] ? index : -1;
      })();

  const mealIndex =
    dayIndex >= 0 && mealPlannerMeal
      ? (selectedWeek[dayIndex].meals || []).findIndex(
          ({ type }) => type.toString() === mealPlannerMeal.toString()
        )
      : -1;

  return { dayIndex, mealIndex };
};

/**
 * Function to get the foods that already exists in a day
 * @param {*} selectedWeek
 * @param {*} mealPlannerDate
 * @param {*} mealPlannerMeal
 * @returns
 */
export const getFilteredFoodsForAddModal = (
  selectedWeek,
  mealPlannerDate,
  mealPlannerMeal
) => {
  const { dayIndex, mealIndex } = getMealLocationForAddModal(
    selectedWeek,
    mealPlannerDate,
    mealPlannerMeal
  );
  if (mealIndex < 0) return [];

  return parseFoodData(selectedWeek[dayIndex].meals[mealIndex].foods || []);
};

/**
 * Get the ingredients of an external recipe stored in a detailed meal plan (client side).
 * Old recipe copies get their ingredient nutrients filled in (and saved) by the API.
 * @param {string} mealPlanId
 * @param {{ weekIndex: number, dayIndex: number, mealIndex: number, foodIndex: number }} indexes
 * @param {string} [proxyAthlete]
 * @returns {Promise<{ food: object, ingredientDetails: "complete" | "unavailable" }>}
 * @throws {Error} with `.status` (e.g. 409 when the plan changed)
 */
export const getMealPlanRecipeIngredients = (
  mealPlanId,
  indexes,
  proxyAthlete
) =>
  clientFetch("/actions/meal-planner/recipe-ingredients", {
    query: {
      mealPlanId,
      ...indexes,
      ...(proxyAthlete ? { proxyAthlete } : {}),
    },
  });

/**
 * Whether the API can rescale an external recipe ingredient (same rules as the API):
 * it needs a positive amount to scale from and nutrients to scale
 * @param {{ amount?: number|null, nutrients?: object }} ingredient
 * @returns {boolean}
 */
export const canEditRecipeIngredient = (ingredient) =>
  Number(ingredient?.amount) > 0 &&
  Boolean(ingredient?.nutrients) &&
  typeof ingredient.nutrients === "object";

/**
 * Portion of an external recipe eaten in a meal plan entry.
 * Ingredient amounts are stored for the whole recipe and ingredient nutrients per serving,
 * so both need converting to show an ingredient for the portion in the plan.
 * @param {{ servings?: number, macrosBase?: "serving"|"batch" }} recipe
 * @param {number|string} quantity meal plan quantity (servings, or batches when macrosBase is "batch")
 * @returns {{ servingsEaten: number, amountRatio: number }} amountRatio turns a whole-recipe amount into the portion's amount
 */
export const getRecipePortion = (recipe, quantity) => {
  const servings = Number(recipe?.servings) > 0 ? Number(recipe.servings) : 1;
  const portion = Number(quantity) > 0 ? Number(quantity) : 1;
  const servingsEaten =
    recipe?.macrosBase === "batch" ? portion * servings : portion;
  return { servingsEaten, amountRatio: servingsEaten / servings };
};

/**
 * Nutrients of an external recipe ingredient scaled to a new amount, same as the API
 * (nutrients scale linearly with the amount, rounded to 2 decimals)
 * @param {{ amount: number, nutrients: object }} ingredient stored ingredient
 * @param {number|string} newAmount amount typed by the user (whole recipe)
 * @returns {object} the stored nutrients when newAmount isn't a positive number
 */
export const scaleRecipeIngredientNutrients = (ingredient, newAmount) => {
  const { amount, nutrients = {} } = ingredient || {};
  const value = Number(newAmount);
  if (newAmount === "" || !(value > 0) || !(Number(amount) > 0)) {
    return nutrients;
  }
  const factor = value / Number(amount);
  return Object.fromEntries(
    ["kcal", "proteins", "carbs", "fats", "fibers"].map((key) => [
      key,
      Math.round((Number(nutrients[key]) || 0) * factor * 100) / 100,
    ])
  );
};

const RECIPE_INGREDIENT_NUTRIENT_KEYS = [
  "kcal",
  "proteins",
  "carbs",
  "fats",
  "fibers",
];
const RECIPE_NUTRIENT_KEYS = ["proteins", "carbs", "fats", "fibers"];
const round2 = (value) => Math.round(value * 100) / 100;

/**
 * Apply ingredient amount edits to an external recipe copy, the same way the API does on Save
 * (setExternalRecipeIngredientAmount): the ingredient's nutrients scale with its amount and the
 * difference is added to the recipe's per-serving nutrients and kcal.
 * @param {object} recipe stored recipe copy (amounts for the whole recipe, nutrients per serving)
 * @param {{ ingredientIndex: number, amount: number }[]} edits whole-recipe amounts
 * @returns {object} a new recipe; edits that can't be applied are skipped
 */
export const applyRecipeIngredientEdits = (recipe, edits = []) => {
  let current = recipe;
  for (const { ingredientIndex, amount } of edits) {
    const ingredient = current?.ingredients?.[ingredientIndex];
    if (!canEditRecipeIngredient(ingredient) || !(Number(amount) > 0)) {
      continue;
    }
    const factor = Number(amount) / Number(ingredient.amount);
    const nutrients = Object.fromEntries(
      RECIPE_INGREDIENT_NUTRIENT_KEYS.map((key) => [
        key,
        round2((Number(ingredient.nutrients[key]) || 0) * factor),
      ])
    );
    const delta = (key) =>
      nutrients[key] - (Number(ingredient.nutrients[key]) || 0);

    const updated = {
      ...current,
      ingredients: current.ingredients.map((item, index) =>
        index === ingredientIndex
          ? {
              ...item,
              amount: Number(amount),
              quantity: `${round2(Number(amount))}${item.unit ? ` ${item.unit}` : ""}`,
              nutrients,
            }
          : item
      ),
    };
    if (current.nutrients && typeof current.nutrients === "object") {
      updated.nutrients = { ...current.nutrients };
      for (const key of RECIPE_NUTRIENT_KEYS) {
        updated.nutrients[key] = round2(
          Math.max(0, (Number(current.nutrients[key]) || 0) + delta(key))
        );
      }
    }
    if (current.kcal != null && Number.isFinite(Number(current.kcal))) {
      updated.kcal = round2(Math.max(0, Number(current.kcal) + delta("kcal")));
    }
    current = updated;
  }
  return current;
};

/**
 * Same rounding as the API uses for meal plan macros (1 decimal below 1, integers otherwise)
 * @param {*} value
 */
const roundMealPlanMacro = (value) => {
  const num = Number(value);
  if (Number.isNaN(num) || num < 0) return 0;
  return num < 1 ? Number(num.toFixed(1)) : Math.round(num);
};

/**
 * Macros of an external recipe row for a quantity, matching what the API returns for the
 * meal plan after Save (calories from the macros: protein*4 + carbs*4 + fat*9)
 * @param {{ nutrients?: object, servings?: number, macrosBase?: string }} recipe
 * @param {number|string} quantity meal plan quantity
 * @returns {{ proteins: number, carbs: number, fats: number, fibers: number, calories: number }}
 */
export const getRecipeRowMacros = (recipe, quantity) => {
  const {
    proteins = 0,
    carbs = 0,
    fats = 0,
    fibers = 0,
  } = recipe?.nutrients || {};
  const calories = fats * 9 + proteins * 4 + carbs * 4;
  const servings = Number(recipe?.servings) > 0 ? Number(recipe.servings) : 1;
  const factor =
    recipe?.macrosBase === "batch"
      ? Number(quantity) / servings
      : Number(quantity);
  return {
    proteins: roundMealPlanMacro(proteins * factor),
    carbs: roundMealPlanMacro(carbs * factor),
    fats: roundMealPlanMacro(fats * factor),
    fibers: roundMealPlanMacro(fibers * factor),
    calories: roundMealPlanMacro(calories * factor),
  };
};

/**
 * Why the ingredients of an external recipe row can't be edited
 * @param {{ type?: string, foodIndex?: number }} item row of the Food modal
 * @param {{ status: string, food?: object, ingredientDetails?: string }} [entry] loaded ingredients
 * @param {boolean} isSupported false when the meal can't be located by the API (simplified plans)
 * @returns {"notSupported"|"notSaved"|"loading"|"error"|"unavailable"|"notEditable"|null} null when editable
 */
export const getRecipeIngredientsBlocker = (item, entry, isSupported) => {
  if (!isSupported) return "notSupported";
  if (item?.foodIndex == null) return "notSaved";
  if (!entry || entry.status === "loading") return "loading";
  if (entry.status === "error") return "error";
  if (entry.ingredientDetails !== "complete") return "unavailable";
  const ingredients = entry.food?.recipe?.ingredients || [];
  return ingredients.some(canEditRecipeIngredient) ? null : "notEditable";
};

export const CALORIES_PER_GRAM = {
  proteins: 4,
  carbs: 4,
  fats: 9,
  fibers: 0,
};
